import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Calendar, CreditCard, Banknote, QrCode, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AdminTransactions = () => {
  const localDateISO = () => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [bookingDate, setBookingDate] = useState(localDateISO());
  const [method, setMethod] = useState('all');
  const [transactions, setTransactions] = useState([]);
  const [venuesMap, setVenuesMap] = useState({});
  const [usersMap, setUsersMap] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }

    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch {
      navigate('/admin/login');
      return;
    }

    loadVenues();
    loadUsers();
  }, [navigate]);

  useEffect(() => {
    loadTransactions();
  }, [bookingDate, method]);

  const loadVenues = async () => {
    try {
      const resp = await api.get('/venues');
      const next = {};
      const list = Array.isArray(resp.data) ? resp.data : [];
      for (const v of list) {
        if (!v?.id) continue;
        next[v.id] = v?.name || v.id;
      }
      setVenuesMap(next);
    } catch {
      setVenuesMap({});
    }
  };

  const loadUsers = async () => {
    try {
      const resp = await api.get('/admin/users');
      const list = Array.isArray(resp.data) ? resp.data : [];
      const next = {};
      for (const u of list) {
        if (!u?.id) continue;
        next[u.id] = { name: u?.name || u.id, role: String(u?.role || '').toLowerCase() };
      }
      setUsersMap(next);
    } catch {
      setUsersMap({});
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = { booking_date: bookingDate };
      if (method && method !== 'all') params.method = method;
      const resp = await api.get('/kasir/transactions', { params });
      setTransactions(Array.isArray(resp.data) ? resp.data : []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memuat transaksi');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteTransaction = async (transactionId) => {
    const ok = window.confirm('Hapus transaksi ini?');
    if (!ok) return;
    try {
      await api.delete(`/admin/transactions/${transactionId}`);
      toast.success('Transaksi berhasil dihapus');
      await loadTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus transaksi');
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const resp = await api.get('/admin/export/transactions', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zero_futsal_transactions_${bookingDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal export CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const summary = useMemo(() => {
    const rows = Array.isArray(transactions) ? transactions : [];
    const result = {
      totalCount: rows.length,
      totalPaid: 0,
      cashCount: 0,
      cashTotal: 0,
      transferCount: 0,
      transferTotal: 0,
      qrisCount: 0,
      qrisTotal: 0,
    };
    for (const tx of rows) {
      const m = String(tx?.method || '').toLowerCase();
      const paid = Number(tx?.paid_amount ?? 0);
      result.totalPaid += Number.isFinite(paid) ? paid : 0;
      if (m === 'cash') {
        result.cashCount += 1;
        result.cashTotal += Number.isFinite(paid) ? paid : 0;
      } else if (m === 'transfer') {
        result.transferCount += 1;
        result.transferTotal += Number.isFinite(paid) ? paid : 0;
      } else if (m === 'qris') {
        result.qrisCount += 1;
        result.qrisTotal += Number.isFinite(paid) ? paid : 0;
      }
    }
    return result;
  }, [transactions]);

  const formatTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' });
  };

  const createdByLabel = (createdBy) => {
    const id = String(createdBy || '').trim();
    if (!id) return 'Online Booking';
    const u = usersMap?.[id];
    if (!u) return id.slice(0, 8);
    if (u.role === 'customer') return 'Online Booking';
    if (u.role === 'admin') return `${u.name} / via web`;
    if (u.role === 'kasir') return `${u.name} / kasir`;
    return u.name;
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Transaksi</span>
            </div>
            <Link to="/admin/dashboard" className="text-zinc-400 hover:text-primary font-medium transition-colors flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white">Transaksi</h1>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2">
                <Calendar className="w-4 h-4 text-zinc-400" />
                <input
                  type="date"
                  value={bookingDate}
                  onChange={(e) => setBookingDate(e.target.value)}
                  className="bg-transparent text-white text-sm outline-none"
                />
              </div>

              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2 text-white text-sm outline-none"
              >
                <option value="all">Semua Metode</option>
                <option value="cash">Cash</option>
                <option value="transfer">Transfer</option>
                <option value="qris">QRIS</option>
              </select>

              <button
                onClick={exportCsv}
                disabled={exporting}
                className="bg-primary text-black hover:bg-primary-hover rounded-none skew-btn px-6 py-2 font-bold uppercase tracking-wider transition-all duration-200 disabled:opacity-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {exporting ? 'Export...' : 'Export CSV'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
                <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <CreditCard className="w-8 h-8 text-primary" />
                    <span className="text-2xl font-black text-white">{summary.totalCount}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Total Transaksi</div>
                  <div className="text-lg font-bold text-primary">{formatCurrency(summary.totalPaid)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-green-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Banknote className="w-8 h-8 text-green-500" />
                    <span className="text-2xl font-black text-white">{summary.cashCount}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Cash</div>
                  <div className="text-lg font-bold text-green-400">{formatCurrency(summary.cashTotal)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-blue-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <CreditCard className="w-8 h-8 text-blue-500" />
                    <span className="text-2xl font-black text-white">{summary.transferCount}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Transfer</div>
                  <div className="text-lg font-bold text-blue-400">{formatCurrency(summary.transferTotal)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-purple-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <QrCode className="w-8 h-8 text-purple-500" />
                    <span className="text-2xl font-black text-white">{summary.qrisCount}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">QRIS</div>
                  <div className="text-lg font-bold text-purple-400">{formatCurrency(summary.qrisTotal)}</div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-zinc-800">
                    <thead className="bg-black">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Waktu</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Booking</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Lapangan</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Created By</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Metode</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-zinc-400 uppercase tracking-wider">Bayar</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-zinc-400 uppercase tracking-wider">Total</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-zinc-400 uppercase tracking-wider">Diskon</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-zinc-400 uppercase tracking-wider">Voucher</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-zinc-400 uppercase tracking-wider">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="px-4 py-10 text-center text-sm text-zinc-500">
                            Tidak ada transaksi untuk tanggal ini.
                          </td>
                        </tr>
                      ) : (
                        transactions.map((tx) => (
                          <tr key={tx.id}>
                            <td className="px-4 py-3 text-sm text-zinc-300">{formatTime(tx.created_at)}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300">{String(tx.booking_id || '').slice(0, 8)}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300">{venuesMap?.[tx.venue_id] || String(tx.venue_id || '').slice(0, 8)}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300">{createdByLabel(tx.created_by)}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300 uppercase">{String(tx.method || '-')}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300 text-right">{formatCurrency(tx.paid_amount)}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300 text-right">{formatCurrency(tx.total_amount)}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300 text-right">{formatCurrency(tx.discount_amount)}</td>
                            <td className="px-4 py-3 text-sm text-zinc-300">{tx.voucher_code || '-'}</td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => deleteTransaction(tx.id)}
                                className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-bold text-xs uppercase"
                              >
                                <Trash2 className="w-4 h-4" />
                                Hapus
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminTransactions;
